/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { UnitPositionType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface UnitPositionSnapshotProps {
  id: string;
  firstName: string;
  lastName: string;
  type: UnitPositionType;
}

export class UnitPositionSnapshotRecord extends Record<UnitPositionSnapshotProps>({
  id: null as any,
  firstName: null as any,
  lastName: null as any,
  type: null as any,
}) {
  public static fromJS(json: {
    id: string;
    firstName: string;
    lastName: string;
    type: UnitPositionType;
  }): UnitPositionSnapshotRecord {
    if (json == null) {
      throw new Error('UnitPositionSnapshotRecord.fromJS: parameter cannot be null');
    }
    return new UnitPositionSnapshotRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'UnitPositionSnapshotJson.id',
      ),
      firstName: getFromOrThrow(json, (j) => j.firstName, 'UnitPositionSnapshotJson.firstName'),
      lastName: getFromOrThrow(json, (j) => j.lastName, 'UnitPositionSnapshotJson.lastName'),
      type: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'type', UnitPositionType.Pilot),
        'UnitPositionSnapshotJson.type',
      ),
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    firstName: string;
    lastName: string;
    type: UnitPositionType;
  }): [UnitPositionSnapshotRecord, Error[]] {
    if (json == null) {
      throw new Error('UnitPositionSnapshotRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new UnitPositionSnapshotRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'UnitPositionSnapshotJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      firstName: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.firstName, 'UnitPositionSnapshotJson.firstName'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastName: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.lastName, 'UnitPositionSnapshotJson.lastName'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      type: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'type', UnitPositionType.Pilot),
            'UnitPositionSnapshotJson.type',
          ),
        (err) => errors.push(err),
      )!,
    });

    return [record, errors];
  }

  constructor(props: UnitPositionSnapshotProps) {
    super(props);
    if (props == null) {
      throw new Error('UnitPositionSnapshotRecord constructor parameter cannot be null');
    }
  }
}

export abstract class UnitPositionSnapshotViewModelBase implements UnitPositionSnapshotProps {
  protected readonly _props: UnitPositionSnapshotProps;
  private _json?: UnitPositionSnapshotProps;

  get id(): string {
    return this._props.id;
  }
  get firstName(): string {
    return this._props.firstName;
  }
  get lastName(): string {
    return this._props.lastName;
  }
  get type(): UnitPositionType {
    return this._props.type;
  }

  public get isClinician() {
    return this._props.type === UnitPositionType.Clinician;
  }
  public get isClinicianTrainee() {
    return this._props.type === UnitPositionType.ClinicianTrainee;
  }
  public get isCoPilot() {
    return this._props.type === UnitPositionType.CoPilot;
  }
  public get isDriver() {
    return this._props.type === UnitPositionType.Driver;
  }
  public get isMechanic() {
    return this._props.type === UnitPositionType.Mechanic;
  }
  public get isNurse() {
    return this._props.type === UnitPositionType.Nurse;
  }
  public get isObserver() {
    return this._props.type === UnitPositionType.Observer;
  }
  public get isOther() {
    return this._props.type === UnitPositionType.Other;
  }
  public get isParamedic() {
    return this._props.type === UnitPositionType.Paramedic;
  }
  public get isPilot() {
    return this._props.type === UnitPositionType.Pilot;
  }
  public get isPilotTrainee() {
    return this._props.type === UnitPositionType.PilotTrainee;
  }

  public isAnyType(...unitPositionType: UnitPositionType[]) {
    return unitPositionType.includes(this._props.type);
  }

  constructor(props: UnitPositionSnapshotProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        firstName: this._props.firstName,
        lastName: this._props.lastName,
        type: this._props.type,
      };
    }
    return this._json;
  }
}
